// src/components/DescriptionEditor/DescriptionEditor.jsx
import React, { useState } from 'react';
import { 
  Editor, 
  EditorState, 
  RichUtils, 
  convertToRaw,
  ContentState,
  convertFromHTML
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'draft-js/dist/Draft.css';
import { Highlighter, List, ListOrdered } from 'lucide-react';

// Custom styles for highlighting
const styleMap = {
  'HIGHLIGHT': {
    backgroundColor: '#ffeb3b',
  },
  'RED_TEXT': {
    color: '#ff0000',
  },
  'GREEN_TEXT': {
    color: '#00c853',
  },
  'BLUE_TEXT': {
    color: '#2196f3',
  },
};

const blockStyleFn = (contentBlock) => {
  const type = contentBlock.getType();
  if (type === 'blockquote') {
    return 'superFancyBlockquote';
  }
  return null;
};

const DescriptionEditor = ({ onChange, initialDescription }) => {
  const [editorState, setEditorState] = useState(() => {
    if (initialDescription) {
      // Convert HTML to ContentState
      const blocksFromHTML = convertFromHTML(initialDescription);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });
  const [showColorOptions, setShowColorOptions] = useState(false);

  const handleEditorChange = (newEditorState) => {
    setEditorState(newEditorState);
    // Convert content to HTML and pass it to parent component
    const contentState = newEditorState.getCurrentContent();
    
    // Add custom style conversions for HTML export
    const options = {
      inlineStyles: {
        HIGHLIGHT: { 
          element: 'mark', // Using mark tag for highlighting
        },
        RED_TEXT: { 
          element: 'span',
          style: { color: '#ff0000' }
        },
        GREEN_TEXT: { 
          element: 'span',
          style: { color: '#00c853' }
        },
        BLUE_TEXT: { 
          element: 'span',
          style: { color: '#2196f3' }
        }
      }
    };
    
    const html = stateToHTML(contentState, options);
    onChange(html);
  };

  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  // Basic formatting
  const onBoldClick = () => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, 'BOLD'));
  };

  const onItalicClick = () => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, 'ITALIC'));
  };

  const onUnderlineClick = () => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'));
  };

  // Lists
  const onBulletListClick = () => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, 'unordered-list-item'));
  };

  const onOrderedListClick = () => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, 'ordered-list-item'));
  };

  // Highlighting and color options
  const onHighlightClick = () => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, 'HIGHLIGHT'));
  };

  const onColorClick = (color) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, color));
    setShowColorOptions(false);
  };

  return (
    <div className="border border-gray-300 rounded-md overflow-hidden">
      <div className="bg-gray-100 p-2 border-b border-gray-300 flex flex-wrap gap-2">
        <button 
          type="button" 
          onClick={onBoldClick}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          <strong>B</strong>
        </button>
        <button 
          type="button" 
          onClick={onItalicClick}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          <em>I</em>
        </button>
        <button 
          type="button" 
          onClick={onUnderlineClick}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          <u>U</u>
        </button>
        <button 
          type="button" 
          onClick={onHighlightClick}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
          style={{ backgroundColor: '#fffde7', borderColor: '#ffeb3b' }}
        >
          <span style={{ backgroundColor: '#ffeb3b', padding: '0 0px', display: 'inline-flex', alignItems: 'center' }}>
            <Highlighter size={16} />
          </span>
        </button>
        <div className="relative hidden">
          <button 
            type="button" 
            onClick={() => setShowColorOptions(!showColorOptions)}
            className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center"
          >
            <span className="mr-1">Colors</span>
            <span className="flex">
              <span className="w-2 h-2 bg-red-500 mr-1"></span>
              <span className="w-2 h-2 bg-green-500 mr-1"></span>
              <span className="w-2 h-2 bg-blue-500"></span>
            </span>
          </button>
          {showColorOptions && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-10">
              <button 
                type="button" 
                onClick={() => onColorClick('RED_TEXT')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                <span style={{ color: '#ff0000' }}>Red Text</span>
              </button>
              <button 
                type="button" 
                onClick={() => onColorClick('GREEN_TEXT')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                <span style={{ color: '#00c853' }}>Green Text</span>
              </button>
              <button 
                type="button" 
                onClick={() => onColorClick('BLUE_TEXT')}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                <span style={{ color: '#2196f3' }}>Blue Text</span>
              </button>
            </div>
          )}
        </div>
        <button 
          type="button" 
          onClick={onBulletListClick}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
          <List size={16} />
        </button>
        <button 
          type="button" 
          onClick={onOrderedListClick}
          className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50"
        >
         <ListOrdered size={16} />
        </button>
      </div>
      <div className="p-2 bg-white" style={{ minHeight: '250px' }}>
        <Editor
          editorState={editorState}
          onChange={handleEditorChange}
          handleKeyCommand={handleKeyCommand}
          placeholder="Enter product description"
          customStyleMap={styleMap}
        />
      </div>
    </div>
  );
};

export default DescriptionEditor;